import { useState, useEffect, useCallback } from 'react';

export interface BreathingConfig {
    inhaleDuration: number;
    holdDuration?: number;
    exhaleDuration: number;
    holdAfterExhale?: number;
    cycles: number;
}

export type Phase = 'idle' | 'inhale' | 'hold' | 'exhale' | 'holdAfter';

export const useBreathingRhythm = (
    config: BreathingConfig,
    onComplete: () => void
) => {
    const [phase, setPhase] = useState<Phase>('idle');
    const [cycleCount, setCycleCount] = useState(0);
    const [isActive, setIsActive] = useState(false);

    const getDuration = useCallback((p: Phase) => {
        switch (p) {
            case 'inhale': return config.inhaleDuration;
            case 'hold': return config.holdDuration || 0;
            case 'exhale': return config.exhaleDuration;
            case 'holdAfter': return config.holdAfterExhale || 0;
            default: return 0;
        }
    }, [config]);

    const handleStop = () => {
        setIsActive(false);
        setPhase('idle');
        setCycleCount(0);
    };

    const handleStart = () => {
        setIsActive(true);
        setCycleCount(0);
        setPhase('idle'); // Will trigger effect to start Inhale
    };

    // Main Loop Effect
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        if (isActive) {
            if (phase === 'idle') {
                // Start immediately
                setPhase('inhale');
            } else {
                const duration = getDuration(phase) * 1000;

                timeoutId = setTimeout(() => {
                    // Transition Logic
                    if (phase === 'inhale') {
                        setPhase(config.holdDuration ? 'hold' : 'exhale');
                    } else if (phase === 'hold') {
                        setPhase('exhale');
                    } else if (phase === 'exhale') {
                        // Check for completion or next hold
                        if (config.holdAfterExhale) {
                            setPhase('holdAfter');
                        } else {
                            // Cycle complete
                            if (cycleCount + 1 >= config.cycles) {
                                handleStop();
                                onComplete();
                            } else {
                                setCycleCount(c => c + 1);
                                setPhase('inhale');
                            }
                        }
                    } else if (phase === 'holdAfter') {
                        // Cycle complete
                        if (cycleCount + 1 >= config.cycles) {
                            handleStop();
                            onComplete();
                        } else {
                            setCycleCount(c => c + 1);
                            setPhase('inhale');
                        }
                    }
                }, duration);
            }
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isActive, phase, config, cycleCount, onComplete, getDuration]);

    return {
        phase,
        cycleCount,
        isActive,
        handleStart,
        handleStop,
        getDuration
    };
};
